import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

const CodeEditor = () => {
  const [code] = useState(`// Example API Integration
const chatbot = require('@openai/api');
const langchain = require('langchain');
const zapier = require('zapier-platform-core');

async function handleUserMessage(message) {
  // Connect to OpenAI GPT-4
  const response = await chatbot.complete({
    model: "gpt-4",
    messages: [{ role: "user", content: message }]
  });

  // Process with LangChain
  const chain = langchain.load('conversation');
  const enhanced = await chain.process(response);

  // Trigger Zapier workflow
  await zapier.run('trigger-slack-notification', {
    message: enhanced.summary
  });

  return enhanced.response;
}`);

  const [currentLine, setCurrentLine] = useState(0);
  const lines = code.split('\n');

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentLine(prev => (prev < lines.length - 1 ? prev + 1 : 0));
    }, 200);

    return () => clearInterval(timer);
  }, [lines.length]);

  return (
    <div className="relative bg-gray-900 rounded-lg overflow-hidden">
      {/* Editor Controls */}
      <div className="flex items-center gap-2 px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="w-3 h-3 rounded-full bg-red-500" />
        <div className="w-3 h-3 rounded-full bg-yellow-500" />
        <div className="w-3 h-3 rounded-full bg-green-500" />
        <span className="ml-4 text-gray-400 text-sm font-mono">chatbot.js</span>
      </div>

      {/* Code Content */}
      <div className="p-6 font-mono text-sm">
        {lines.map((line, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ 
              opacity: index <= currentLine ? 1 : 0.3,
              x: 0,
              color: index === currentLine ? '#FF6A00' : '#E5E7EB'
            }}
            className="whitespace-pre"
          >
            {line}
          </motion.div>
        ))}
      </div>

      {/* Technology Pills */}
      <div className="absolute top-4 right-4 flex flex-wrap gap-2">
        {['OpenAI', 'LangChain', 'Zapier', 'Make.com', 'Slack'].map((tech) => (
          <motion.span
            key={tech}
            className="px-2 py-1 text-xs rounded-full bg-neon-orange/20 text-neon-orange"
            whileHover={{ scale: 1.05 }}
          >
            {tech}
          </motion.span>
        ))}
      </div>
    </div>
  );
};

const ChatInterface = () => {
  const messages = [
    { role: 'user', content: 'Bonjour, j\'ai besoin d\'un site e-commerce performant' },
    { role: 'assistant', content: 'Bonjour 👋 Je peux vous aider à créer une boutique optimisée avec paiement Klarna et SEO intégré !' },
    { role: 'user', content: 'Quelles sont les fonctionnalités incluses ?' },
    { role: 'assistant', content: 'Votre boutique comprendra :\n• Paiement sécurisé Klarna\n• SEO optimisé\n• Design responsive\n• Dashboard analytics\n• Gestion des stocks\n• Intégration CRM' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-xl overflow-hidden">
      {/* Chat Header */}
      <div className="bg-gray-50 p-4 border-b">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-neon-orange/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-neon-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold">Assistant IA</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                En ligne
              </span>
              <span>|</span>
              <span>Temps de réponse &lt; 1min</span>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="h-[400px] overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl p-4 ${
                message.role === 'user'
                  ? 'bg-neon-orange text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              <p className="whitespace-pre-line">{message.content}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Chat Input */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Écrivez votre message..."
            className="flex-1 px-4 py-2 rounded-full border focus:outline-none focus:border-neon-orange"
          />
          <button className="bg-neon-orange text-white px-6 py-2 rounded-full hover:bg-neon-orange/90 transition-colors">
            Envoyer
          </button>
        </div>
      </div>

      {/* Technology Pills */}
      <div className="px-4 pb-4 flex flex-wrap gap-2">
        {['GPT-4', 'Whisper', 'DALL·E', 'Node.js'].map((tech) => (
          <motion.span
            key={tech}
            className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-600"
            whileHover={{ scale: 1.05 }}
          >
            {tech}
          </motion.span>
        ))}
      </div>
    </div>
  );
};

const CodeChatSection = () => {
  return (
    <section className="py-12 sm:py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-20 right-20 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-4xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-gray-900 via-neon-orange to-gray-900 bg-clip-text text-transparent">
            La puissance de l'IA à votre service
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Découvrez comment notre technologie combine expertise technique et intelligence artificielle
            pour créer des solutions sur mesure.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {/* Code Editor Side */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="order-2 md:order-1"
          >
            <CodeEditor />
          </motion.div>

          {/* Chat Interface Side */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="order-1 md:order-2"
          >
            <ChatInterface />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CodeChatSection;